import { useState } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BusinessIcon from "@mui/icons-material/Business";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DomainIcon from "@mui/icons-material/Domain";
import LocationOnIcon from "@mui/icons-material/LocationOn";

const getIcon = (tiposContactosCFE) => {
  const iconStyle = { fontSize: 18, marginRight: 8 };

  switch (tiposContactosCFE) {
    case "OFICINA_OFICIAL":
      return <BusinessIcon style={{ ...iconStyle, color: "#1976d2" }} />;
    case "DEPARTAMENTO_CFE":
      return <AccountTreeIcon style={{ ...iconStyle, color: "#2e7d32" }} />;
    case "DIVISIONCFE":
      return <DomainIcon style={{ ...iconStyle, color: "#ed6c02" }} />;
    case "SUPERINTENDENCIASZONA":
      return <LocationOnIcon style={{ ...iconStyle, color: "#757575" }} />;
    default:
      return null;
  }
};

const generateItemId = (node, parentPath = "") => {
  return parentPath
    ? `${parentPath}/${node.tiposContactosCFE}-${node.id}`
    : `${node.tiposContactosCFE}-${node.id}`;
};

const TreeItem = ({
  node,
  parentPath = "",
  level = 0,
  expandedItems,
  toggleExpand,
  onItemClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const itemId = generateItemId(node, parentPath);
  const hasChildren = node.organigramasCFE && node.organigramasCFE.length > 0;
  const isExpanded = expandedItems.has(itemId);

  const handleClick = () => {
    if (onItemClick) {
      onItemClick(node);
    }
    if (hasChildren) {
      toggleExpand(itemId);
    }
  };

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    padding: "6px 8px",
    paddingLeft: level * 20 + 8,
    cursor: "pointer",
    userSelect: "none",
    borderRadius: 4,
    backgroundColor: isHovered ? "#f5f5f5" : "transparent",
    transition: "background-color 0.15s ease",
  };

  const iconContainerStyle = {
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 4,
    color: "#9e9e9e",
  };

  const labelStyle = {
    fontSize: 14,
    color: "#333",
  };

  return (
    <div>
      <div
        style={rowStyle}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span style={iconContainerStyle}>
          {hasChildren ? (
            isExpanded ? (
              <ExpandMoreIcon style={{ fontSize: 20 }} />
            ) : (
              <ChevronRightIcon style={{ fontSize: 20 }} />
            )
          ) : (
            <span style={{ width: 20 }} />
          )}
        </span>
        {getIcon(node.tiposContactosCFE)}
        <span style={labelStyle}>{node.nombreDepartamento}</span>
      </div>

      {hasChildren && isExpanded && (
        <div>
          {node.organigramasCFE.map((child) => (
            <TreeItem
              key={generateItemId(child, itemId)}
              node={child}
              parentPath={itemId}
              level={level + 1}
              expandedItems={expandedItems}
              toggleExpand={toggleExpand}
              onItemClick={onItemClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const getItemIdsByLevel = (
  nodes,
  maxLevel,
  parentPath = "",
  currentLevel = 0
) => {
  const ids = [];
  if (currentLevel >= maxLevel) return ids;

  nodes.forEach((node) => {
    const itemId = generateItemId(node, parentPath);
    ids.push(itemId);
    if (node.organigramasCFE && node.organigramasCFE.length > 0) {
      ids.push(
        ...getItemIdsByLevel(
          node.organigramasCFE,
          maxLevel,
          itemId,
          currentLevel + 1
        )
      );
    }
  });

  return ids;
};

const getAllItemIds = (nodes, parentPath = "") => {
  const ids = [];
  nodes.forEach((node) => {
    const itemId = generateItemId(node, parentPath);
    ids.push(itemId);
    if (node.organigramasCFE && node.organigramasCFE.length > 0) {
      ids.push(...getAllItemIds(node.organigramasCFE, itemId));
    }
  });
  return ids;
};

export const getExpandAllItems = (data) => {
  if (!data?.organigramasCFE) return [];
  return getAllItemIds(data.organigramasCFE);
};

export const getExpandedItemsByLevel = (data, maxLevel) => {
  if (!data?.organigramasCFE) return [];
  return getItemIdsByLevel(data.organigramasCFE, maxLevel);
};

const OrganigramaCFE = ({ data, defaultExpandedLevel = 0, onItemClick }) => {
  const [expandedItems, setExpandedItems] = useState(() => {
    if (!data?.organigramasCFE) return new Set();
    return new Set(
      getItemIdsByLevel(data.organigramasCFE, defaultExpandedLevel)
    );
  });

  const toggleExpand = (itemId) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const expandAll = () => {
    if (data?.organigramasCFE) {
      setExpandedItems(new Set(getAllItemIds(data.organigramasCFE)));
    }
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  const containerStyle = {
    backgroundColor: "#f5f5f5",
    padding: "12px 0",
  };

  const cardStyle = {
    margin: "0 auto",
    backgroundColor: "#fff",
    borderRadius: 8,
    boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
    border: "1px solid #e0e0e0",
  };

  const headerStyle = {
    padding: "12px 16px",
    borderBottom: "1px solid #e0e0e0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const titleStyle = {
    fontSize: 18,
    fontWeight: 600,
    color: "#333",
    margin: 0,
  };

  const buttonContainerStyle = {
    display: "flex",
    gap: 8,
  };

  const buttonStyle = {
    padding: "6px 12px",
    fontSize: 14,
    backgroundColor: "#f5f5f5",
    border: "1px solid #e0e0e0",
    borderRadius: 4,
    cursor: "pointer",
    color: "#333",
    transition: "background-color 0.15s ease",
  };

  const treeContainerStyle = {
    padding: 8,
    maxHeight: 500,
    overflowY: "auto",
  };

  if (!data?.organigramasCFE || data.organigramasCFE.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ padding: 16, color: "#757575" }}>
            No hay datos disponibles para mostrar.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>Organigrama CFE</h2>
          <div style={buttonContainerStyle}>
            <button
              style={buttonStyle}
              onClick={expandAll}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#e0e0e0")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#f5f5f5")}
            >
              Expandir Todo
            </button>
            <button
              style={buttonStyle}
              onClick={collapseAll}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#e0e0e0")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#f5f5f5")}
            >
              Colapsar Todo
            </button>
          </div>
        </div>

        <div style={treeContainerStyle}>
          {data.organigramasCFE.map((node) => (
            <TreeItem
              key={generateItemId(node)}
              node={node}
              expandedItems={expandedItems}
              toggleExpand={toggleExpand}
              onItemClick={onItemClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default OrganigramaCFE;
